<template>
  <div class="payment-sidebar p-4 h-100 d-flex flex-column justify-content-between">
    <div>
      <h5 class="sidebar-title fw-bold mb-4">RESUMEN DE PAGO</h5>
      
      <!-- Summary details -->
      <div class="summary-details mb-4">
        <div class="d-flex justify-content-between mb-2">
          <span class="summary-label">Subtotal USD</span>
          <span class="summary-value fw-bold">${{ parseFloat(subtotalUSD).toFixed(2) }}</span>
        </div>
        <div class="d-flex justify-content-between mb-2">
          <span class="summary-label">Tasa BCV</span>
          <span class="summary-value">{{ parseFloat(bcvRate || 0).toFixed(2) }} Bs.</span>
        </div>
        <hr class="summary-divider" />
        
        <div class="total-to-pay-container p-3 bg-white rounded-3 mb-4 mt-3">
          <span class="total-label d-block text-uppercase fw-semibold mb-1">TOTAL A PAGAR</span>
          <span class="total-value-bs fw-bold d-block text-dark mb-1">
            {{ parseFloat(totalPayableBs).toLocaleString('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }} Bs.
          </span>
          <span class="total-value-usd text-muted d-block">${{ parseFloat(subtotalUSD).toFixed(2) }} USD</span>
        </div>
      </div>
      
      <!-- Form inputs -->
      <div class="form-inputs-container">
          <!-- Método de pago & Monto -->
        <div class="row g-2 mb-2">
          <div class="col-6">
            <span class="input-label-custom">MÉTODO DE PAGO</span>
            <multiselect
              :model-value="modelValue.paymentMethod"
              @update:model-value="updateField('paymentMethod', $event)"
              :options="['pago_movil', 'transferencia']"
              :custom-label="opt => opt === 'pago_movil' ? 'Pago Móvil' : 'Transferencia'"
              placeholder="Método"
              class="custom-multiselect mt-1"
              :allow-empty="false"
            ></multiselect>
          </div>
          <div class="col-6">
            <span class="input-label-custom">MONTO</span>
            <validated-input
              :model-value="modelValue.amountWithPrefix"
              @update:model-value="updateField('amountWithPrefix', $event)"
              type="text"
              placeholder="Monto"
              id="sidebar-amount"
              :is-currency="true"
              class="mt-1 custom-validated-input-sidebar"
            />
          </div>
        </div>

        <!-- Disclaimer de validación del método de pago -->
        <div
          v-if="modelValue.paymentMethod"
          class="payment-method-disclaimer mb-3 p-2 rounded d-flex align-items-center"
          :class="modelValue.paymentMethod === 'pago_movil' ? 'disclaimer-pago-movil' : 'disclaimer-transferencia'"
        >
          <span v-if="modelValue.paymentMethod === 'pago_movil'">
 El pago móvil se valida automáticamente.
          </span>
          <span v-else-if="modelValue.paymentMethod === 'transferencia'">
La transferencia no se validará automáticamente.
          </span>
        </div>
        
        <!-- Reusable bank details info (visible only in mobile layouts) -->
        <div class="d-lg-none" v-if="modelValue.selectedCompanyBank">
          <BankDetailsInfo
            :bank-details="modelValue.selectedCompanyBank"
            :payment-method="modelValue.paymentMethod"
            theme="light"
            class="mb-3"
          />
        </div>

             <!-- Banco destino -->
        <div class="mb-3">
          <span class="input-label-custom">BANCO DESTINO</span>
          <multiselect
            :model-value="modelValue.selectedCompanyBank"
            @update:model-value="updateField('selectedCompanyBank', $event)"
            label="name_bank"
            track-by="id"
            :options="companyBank"
            :searchable="true"
            placeholder="Selecciona banco destino"
            class="custom-multiselect mt-1"
            :allow-empty="true"
          ></multiselect>
        </div>
        
        <!-- Fecha de pago & Moneda -->
       <div class="row g-2 mb-3">

      
          <div class="col-6">
            <span class="input-label-custom">FECHA DE PAGO</span>
            <div class="datepicker-wrapper">
              <date-picker 
                :model-value="parsedPaymentDate" 
                @update:model-value="updateDateField"
                :upper-limit="maxDateLimit"
                :locale="es"
                input-class="form-control form-control-custom mt-1" 
                placeholder="Seleccionar fecha"
              />
            </div>
          </div>
          <div class="col-6">
            <span class="input-label-custom">MONEDA</span>
            <multiselect
              :model-value="modelValue.currency"
              @update:model-value="updateField('currency', $event)"
              :options="modelValue.currencyOptions ? modelValue.currencyOptions.map(c => c.code) : ['VES', 'USD']"
              :custom-label="opt => modelValue.currencyOptions?.find(c => c.code === opt)?.name || opt"
              placeholder="Moneda"
              class="custom-multiselect mt-1"
              :allow-empty="false"
            ></multiselect>
          </div>
        </div>

   
        <!-- Teléfono (Pago Móvil) -->
        <div class="mb-3" v-if="modelValue.paymentMethod === 'pago_movil'">
          <span class="input-label-custom">TELÉFONO</span>
          <validated-input
            :model-value="modelValue.phone"
            @update:model-value="updateField('phone', $event)"
            type="tel"
            placeholder="Ej: 04141234567"
            id="sidebar-phone"
            :rules="[(v) => /^[0-9]{11}$/.test(v) || 'Teléfono debe tener 11 dígitos']"
            class="mt-1"
          />
        </div>

        <!-- Banco emisor & Referencia (Solo si hay Banco Destino seleccionado) -->
        <div class="row g-2 mb-3" v-if="modelValue.selectedCompanyBank">
          <div class="col-6">
            <span class="input-label-custom">BANCO EMISOR</span>
            <multiselect
              :model-value="modelValue.originBank"
              @update:model-value="updateField('originBank', $event)"
              label="name"
              track-by="id"
              :options="banksNationals"
              :searchable="true"
              placeholder="Banco emisor"
              class="custom-multiselect mt-1"
              :allow-empty="false"
            ></multiselect>
          </div>
          <div class="col-6">
            <span class="input-label-custom">REFERENCIA</span>
            <validated-input
              :model-value="modelValue.reference"
              @update:model-value="updateField('reference', $event)"
              type="number"
              placeholder="Referencia"
              id="sidebar-reference"
              :rules="[
                (v) => v && v.length >= 4 || 'Referencia debe tener al menos 4 dígitos'
              ]"
              class="mt-1"
            />
          </div>
        </div>

        <!-- Subir comprobante -->
        <div class="mb-4" v-if="modelValue.selectedCompanyBank">
          <span class="input-label-custom">SUBIR COMPROBANTE</span>
          <div class="mt-1">
            <file-pond
              ref="filePondRef"
              name="capture"
              :model-value="modelValue.urlImage"
              @update:model-value="updateField('urlImage', $event)"
              label-idle="<i class='fas fa-cloud-upload-alt fs-4 mb-2 d-block text-secondary'></i>Subir comprobante o captura"
              class="custom-filepond"
            ></file-pond>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Action buttons -->
    <div class="action-buttons-sidebar mt-3">
      <button
        type="button"
        class="btn report-btn w-100 py-3 mb-2 fw-bold"
        @click.prevent="handleSubmit"
      >
        REPORTAR PAGO &nbsp; <i class="fas fa-arrow-right"></i>
      </button>
      <button
        type="button"
        class="btn btn-link text-uppercase text-decoration-none text-muted w-100 fw-bold cancel-btn"
        @click.prevent="$emit('cancel')"
      >
        CANCELAR
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import Multiselect from 'vue-multiselect';
import BankDetailsInfo from './BankDetailsInfo.vue';
import { es } from 'date-fns/locale';
import vueFilePond from 'vue-filepond';
import 'filepond/dist/filepond.min.css';
import 'filepond-plugin-image-preview/dist/filepond-plugin-image-preview.min.css';
import FilePondPluginFileValidateType from 'filepond-plugin-file-validate-type';
import FilePondPluginImagePreview from 'filepond-plugin-image-preview';
import Swal from 'sweetalert2';
import 'sweetalert2/src/sweetalert2.scss';

const FilePond = vueFilePond(
  FilePondPluginFileValidateType,
  FilePondPluginImagePreview
);

const maxDate = computed(() => {
  return new Date().toISOString().split('T')[0];
});

const props = defineProps({
  modelValue: {
    type: Object,
    required: true
  },
  companyBank: {
    type: Array,
    default: () => []
  },
  banksNationals: {
    type: Array,
    default: () => []
  },
  bcvRate: {
    type: [Number, String],
    default: 0
  },
  subtotalUSD: {
    type: [Number, String],
    default: 0
  },
  totalPayableBs: {
    type: [Number, String],
    default: 0
  }
});

const emit = defineEmits(['update:modelValue', 'submit', 'cancel']);

const filePondRef = ref(null);

const updateField = (field, value) => {
  emit('update:modelValue', {
    ...props.modelValue,
    [field]: value
  });
};

const maxDateLimit = computed(() => {
  return new Date();
});

const parsedPaymentDate = computed(() => {
  if (!props.modelValue.paymentDate) return null;
  const [year, month, day] = props.modelValue.paymentDate.split('-').map(Number);
  return new Date(year, month - 1, day);
});

const updateDateField = (date) => {
  if (date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    updateField('paymentDate', `${year}-${month}-${day}`);
  } else {
    updateField('paymentDate', '');
  }
};

const getFiles = () => {
  return filePondRef.value?.getFiles() || [];
};

const handleSubmit = () => {
  // Validate file upload if bank is selected
  if (props.modelValue.selectedCompanyBank) {
    const files = getFiles();
    if (!files || files.length === 0) {
      Swal.fire({
        title: 'Campo requerido',
        text: 'Debe subir el comprobante de pago para continuar',
        icon: 'warning',
        confirmButtonColor: '#5a122d',
        confirmButtonText: 'Entendido'
      });
      return;
    }
    // Extract the actual File object from FilePond file item
    const file = files[0]?.file || files[0];
    emit('submit', file);
  } else {
    emit('submit');
  }
};

defineExpose({
  getFiles
});
</script>

<style scoped>
.payment-sidebar {
  background-color: #eef2f6;
  border-top-right-radius: 15px;
  border-bottom-right-radius: 15px;
}

.sidebar-title {
  color: #2d3748;
  font-size: 0.95rem;
  letter-spacing: 0.5px;
}

.summary-label {
  font-size: 0.88rem;
  color: #4a5568;
}

.summary-value {
  font-size: 0.95rem;
  color: #1a202c;
}

.summary-divider {
  border-top: 1px dashed #cbd5e0;
  margin: 10px 0;
  opacity: 1;
}

.total-to-pay-container {
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.03);
}

.total-label {
  font-size: 0.72rem;
  color: #718096;
  letter-spacing: 0.5px;
}

.total-value-bs {
  font-size: 1.6rem;
  color: #5a122d !important;
}

.total-value-usd {
  font-size: 0.85rem;
}

.input-label-custom {
  font-size: 0.72rem;
  color: #4a5568;
  font-weight: bold;
  letter-spacing: 0.3px;
  display: block;
}

.payment-method-disclaimer {
  font-size: 0.78rem;
  line-height: 1.3;
}

.disclaimer-pago-movil {
  background-color: #e6f4ea;
  color: #137333;
  border-left: 3px solid #137333;
}

.disclaimer-transferencia {
  background-color: #fef7e0;
  color: #b06000;
  border-left: 3px solid #b06000;
}

.form-control-custom {
  border-radius: 8px;
  border: 1px solid #cbd5e0;
  height: 38px;
  font-size: 0.88rem;
  background-color: white;
}

.custom-multiselect :deep(.multiselect__tags) {
  border-radius: 8px;
  border: 1px solid #cbd5e0;
  min-height: 38px;
  padding: 6px 40px 0 8px;
}

.custom-multiselect :deep(.multiselect__single) {
  font-size: 0.88rem;
  margin-bottom: 4px;
}

.custom-multiselect :deep(.multiselect__select) {
  height: 36px;
}

/* Custom filepond design matching picture */
.custom-filepond :deep(.filepond--drop-label) {
  background-color: white;
  border: 1px dashed #5a122d;
  border-radius: 8px;
  min-height: 80px;
  cursor: pointer;
}

.custom-filepond :deep(.filepond--label-action) {
  text-decoration: none;
  color: #5a122d;
  font-weight: bold;
}

.report-btn {
  background-color: #5a122d;
  color: white;
  border-radius: 8px;
  font-size: 0.95rem;
  letter-spacing: 0.5px;
  transition: all 0.2s ease;
  border: none;
}

.report-btn:hover {
  background-color: #430c23;
  color: white;
}

.cancel-btn {
  font-size: 0.85rem;
  letter-spacing: 0.5px;
}

.custom-validated-input-sidebar :deep(.mb-3) {
  margin-bottom: 0 !important;
}

.datepicker-wrapper {
  width: 100%;
  display: block;
}

.datepicker-wrapper :deep(.v3dp__datepicker) {
  width: 100%;
  display: block;
  --vdp-selected-bg-color: #5a122d;
  --vdp-hover-bg-color: #5a122d;
  --vdp-selected-color: #fff;
  --vdp-hover-color: #fff;
}

.datepicker-wrapper :deep(.v3dp__datepicker input) {
  width: 100%;
  border-radius: 8px !important;
  border: 1px solid #cbd5e0 !important;
  height: 38px !important;
  font-size: 0.88rem !important;
  background-color: white !important;
  padding: 6px 12px !important;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out !important;
}

.datepicker-wrapper :deep(.v3dp__datepicker input:focus) {
  border-color: #5a122d !important;
  outline: 0 !important;
  box-shadow: 0 0 0 0.2rem rgba(90, 18, 45, 0.25) !important;
}
</style>
